import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, useToolTheme } from '../../hooks/useTheme';
import { storage } from '../../utils/storage';
import SEO from '../../components/SEO/SEO';
import * as XLSX from 'xlsx';
import './ExcelCompare.css';
import Logo3D from '../../components/Logo3D/Logo3D';

let tabIdCounter = 1;
function createTab(name) {
  return { id: tabIdCounter++, name, leftData: null, rightData: null, leftName: '', rightName: '' };
}

function normalizeSheet(sheet) {
  if (!sheet) return [];
  const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const colCount = range.e.c + 1;
  return data.map(row => {
    const normalized = Array.isArray(row) ? [...row] : [];
    while (normalized.length < colCount) normalized.push('');
    return normalized;
  });
}

function parseExcelWorkbook(buffer) {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  return Object.fromEntries(
    workbook.SheetNames.map(sheetName => [sheetName, normalizeSheet(workbook.Sheets[sheetName])])
  );
}

const hasSheet = (sheets, sheetName) => Object.prototype.hasOwnProperty.call(sheets, sheetName);

function ExcelDiff({ tabId }) {
  const savedTab = useRef(storage.get(`ec_tab_${tabId}`) || {}).current;
  const [leftSheets, setLeftSheets] = useState(() => savedTab.leftSheets || (savedTab.leftData ? { Sheet1: savedTab.leftData } : {}));
  const [rightSheets, setRightSheets] = useState(() => savedTab.rightSheets || (savedTab.rightData ? { Sheet1: savedTab.rightData } : {}));
  const [leftSheet, setLeftSheet] = useState(() => savedTab.leftSheet || Object.keys(savedTab.leftSheets || {})[0] || (savedTab.leftData ? 'Sheet1' : ''));
  const [rightSheet, setRightSheet] = useState(() => savedTab.rightSheet || Object.keys(savedTab.rightSheets || {})[0] || (savedTab.rightData ? 'Sheet1' : ''));
  const [leftName, setLeftName] = useState(() => savedTab.leftName || '');
  const [rightName, setRightName] = useState(() => savedTab.rightName || '');
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);
  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const syncingRef = useRef(false);

  const leftSheetNames = useMemo(() => Object.keys(leftSheets), [leftSheets]);
  const rightSheetNames = useMemo(() => Object.keys(rightSheets), [rightSheets]);
  const leftData = leftSheets[leftSheet] || null;
  const rightData = rightSheets[rightSheet] || null;

  const zoomIn = () => setZoom(z => Math.min(z + 10, 200));
  const zoomOut = () => setZoom(z => Math.max(z - 10, 50));
  const zoomReset = () => setZoom(100);

  // Persist data on change
  useEffect(() => {
    try {
      storage.set(`ec_tab_${tabId}`, {
        leftSheets, rightSheets, leftSheet, rightSheet, leftName, rightName
      });
    } catch (e) {
      // localStorage might be full for large spreadsheets
    }
  }, [tabId, leftSheets, rightSheets, leftSheet, rightSheet, leftName, rightName]);

  const parseCSV = (text) => {
    return text.trim().split('\n').map(row => {
      const cells = [];
      let current = '', inQuotes = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { cells.push(current.trim()); current = ''; }
        else { current += ch; }
      }
      cells.push(current.trim());
      return cells;
    });
  };

  const parsePasted = (text) => {
    return text.trim().split('\n').map(line => line.split('\t'));
  };

  const handleTablePaste = useCallback((side) => (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    if (!text.trim()) return;
    const data = text.includes('\t') ? parsePasted(text) : parseCSV(text);
    if (side === 'left') {
      setLeftSheets({ 'Pasted data': data });
      setLeftSheet('Pasted data');
      setLeftName('Pasted data');
    } else {
      setRightSheets({ 'Pasted data': data });
      setRightSheet('Pasted data');
      setRightName('Pasted data');
    }
  }, []);

  const handleFile = useCallback((side) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const applySheets = (sheets) => {
      const sheetNames = Object.keys(sheets);
      const otherSheetNames = side === 'left' ? Object.keys(rightSheets) : Object.keys(leftSheets);
      const currentOtherSheet = side === 'left' ? rightSheet : leftSheet;
      const selectedSheet = sheetNames.includes(currentOtherSheet)
        ? currentOtherSheet
        : sheetNames.find(name => otherSheetNames.includes(name)) || sheetNames[0] || '';

      if (side === 'left') {
        setLeftSheets(sheets);
        setLeftSheet(selectedSheet);
        setLeftName(file.name);
        if (hasSheet(rightSheets, selectedSheet)) setRightSheet(selectedSheet);
      } else {
        setRightSheets(sheets);
        setRightSheet(selectedSheet);
        setRightName(file.name);
        if (hasSheet(leftSheets, selectedSheet)) setLeftSheet(selectedSheet);
      }
    };

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        applySheets(parseExcelWorkbook(ev.target.result));
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = parseCSV(ev.target.result);
        applySheets({ Sheet1: data });
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  }, [leftSheets, rightSheets, leftSheet, rightSheet]);

  const handleScroll = useCallback((source) => (e) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    const target = source === 'left' ? rightScrollRef.current : leftScrollRef.current;
    if (target) {
      target.scrollTop = e.target.scrollTop;
      target.scrollLeft = e.target.scrollLeft;
    }
    setTimeout(() => { syncingRef.current = false; }, 10);
  }, []);

  const maxRows = Math.max(leftData?.length || 0, rightData?.length || 0);
  const maxCols = useMemo(() => {
    let max = 0;
    if (leftData) leftData.forEach(r => { if (r && r.length > max) max = r.length; });
    if (rightData) rightData.forEach(r => { if (r && r.length > max) max = r.length; });
    return max;
  }, [leftData, rightData]);

  const comparison = useMemo(() => {
    if (!leftData || !rightData) {
      return { map: {}, stats: { added: 0, removed: 0, modified: 0, unchanged: 0 } };
    }
    const map = {};
    let added = 0, removed = 0, modified = 0, unchanged = 0;
    for (let row = 0; row < maxRows; row++) {
      const leftRow = leftData[row];
      const rightRow = rightData[row];
      if (!leftRow && rightRow) { map[row] = { type: 'row-added' }; added++; continue; }
      if (leftRow && !rightRow) { map[row] = { type: 'row-removed' }; removed++; continue; }
      let rowHasDiff = false;
      const cells = {};
      for (let col = 0; col < maxCols; col++) {
        const lVal = String(leftRow?.[col] ?? '');
        const rVal = String(rightRow?.[col] ?? '');
        if (lVal !== rVal) { cells[col] = { left: lVal, right: rVal }; rowHasDiff = true; }
      }
      if (rowHasDiff) { map[row] = { type: 'modified', cells }; modified++; }
      else { unchanged++; }
    }
    return { map, stats: { added, removed, modified, unchanged } };
  }, [leftData, rightData, maxRows, maxCols]);
  const diffMap = comparison.map;
  const stats = comparison.stats;

  const getRowClass = (row) => {
    const d = diffMap[row];
    if (!d) return '';
    if (d.type === 'row-added') return 'dc__xrow--added';
    if (d.type === 'row-removed') return 'dc__xrow--removed';
    if (d.type === 'modified') return 'dc__xrow--modified';
    return '';
  };

  const getCellDiff = (row, col) => {
    const d = diffMap[row];
    if (!d || d.type !== 'modified') return null;
    return d.cells?.[col] || null;
  };

  const visibleRows = useMemo(() => {
    if (!showOnlyDiffs) return Array.from({ length: maxRows }, (_, i) => i);
    return Array.from({ length: maxRows }, (_, i) => i).filter(i => diffMap[i]);
  }, [maxRows, showOnlyDiffs, diffMap]);

  const colHeader = (i) => {
    let s = '';
    let n = i;
    while (n >= 0) { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; }
    return s;
  };

  const loadSample = () => {
    setLeftSheets({
      Employees: [
        ['ID', 'Name', 'Department', 'City', 'Salary', 'Status'],
        ['001', 'Alice Johnson', 'Engineering', 'Mumbai', '120000', 'Active'],
        ['002', 'Bob Smith', 'Design', 'Delhi', '95000', 'Active'],
        ['003', 'Charlie Brown', 'Management', 'Bangalore', '150000', 'Active'],
        ['004', 'Diana Prince', 'Analytics', 'Pune', '85000', 'Active'],
        ['005', 'Eve Wilson', 'Engineering', 'Chennai', '110000', 'Active'],
      ],
      Departments: [
        ['Department', 'Head', 'Budget'],
        ['Engineering', 'Alice Johnson', '900000'],
        ['Design', 'Bob Smith', '350000'],
        ['Analytics', 'Diana Prince', '420000'],
      ],
    });
    setRightSheets({
      Employees: [
        ['ID', 'Name', 'Department', 'City', 'Salary', 'Status'],
        ['001', 'Alice Johnson', 'Engineering', 'Mumbai', '145000', 'Active'],
        ['002', 'Bob Smith', 'Design', 'Hyderabad', '95000', 'Active'],
        ['003', 'Charlie Brown', 'Director', 'Bangalore', '180000', 'Active'],
        ['004', 'Diana Prince', 'Analytics', 'Pune', '92000', 'Promoted'],
        ['005', 'Eve Wilson', 'Engineering', 'Chennai', '110000', 'Active'],
        ['006', 'Hank Patel', 'Engineering', 'Pune', '105000', 'Active'],
      ],
      Departments: [
        ['Department', 'Head', 'Budget'],
        ['Engineering', 'Alice Johnson', '950000'],
        ['Design', 'Bob Smith', '350000'],
        ['Analytics', 'Diana Prince', '500000'],
      ],
      Locations: [
        ['City', 'Office'],
        ['Mumbai', 'West'],
        ['Bangalore', 'South'],
      ],
    });
    setLeftSheet('Employees');
    setRightSheet('Employees');
    setLeftName('employees-q1.xlsx');
    setRightName('employees-q2.xlsx');
  };

  const clearAll = () => {
    setLeftSheets({});
    setRightSheets({});
    setLeftSheet('');
    setRightSheet('');
    setLeftName('');
    setRightName('');
  };

  const workbookSheetNames = Array.from(new Set([...leftSheetNames, ...rightSheetNames]));
  const selectMatchingSheet = (sheetName) => {
    if (hasSheet(leftSheets, sheetName)) setLeftSheet(sheetName);
    if (hasSheet(rightSheets, sheetName)) setRightSheet(sheetName);
  };

  const renderTable = (data, side, scrollRef, scrollHandler) => (
    <div className="dc__excel-scroll" ref={scrollRef} onScroll={scrollHandler}>
      <table className="dc__excel-table">
        <thead>
          <tr>
            <th className="dc__excel-rownum">#</th>
            {Array.from({ length: maxCols }, (_, i) => (
              <th key={i} className="dc__excel-colhead">{colHeader(i)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(row => {
            const otherData = side === 'left' ? rightData : leftData;
            return (
              <tr key={row} className={otherData ? getRowClass(row) : ''}>
                <td className="dc__excel-rownum">{row + 1}</td>
                {Array.from({ length: maxCols }, (_, col) => {
                  const val = String(data[row]?.[col] ?? '');
                  const cellDiff = otherData ? getCellDiff(row, col) : null;
                  return (
                    <td key={col} className={`dc__excel-cell ${cellDiff ? 'dc__xcell--changed' : ''}`} title={cellDiff && side === 'right' ? `Was: "${cellDiff.left}"` : ''}>
                      {val}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="dc__excel-diff">
      <div className="dc__excel-toolbar">
        <label className="dc__toolbar-btn" title="Open the original workbook">▣ Open original<input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFile('left')} hidden /></label>
        <label className="dc__toolbar-btn" title="Open the modified workbook">▣ Open modified<input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFile('right')} hidden /></label>
        <span className="dc__toolbar-divider" aria-hidden="true" />
        <button className="dc__toolbar-btn" onClick={loadSample}>▶ Sample data</button>
        {leftData && rightData && (
          <label className="dc__check"><input type="checkbox" checked={showOnlyDiffs} onChange={e => setShowOnlyDiffs(e.target.checked)} /> Show only differences</label>
        )}
        {(leftName || rightName) && <button className="dc__toolbar-btn" onClick={clearAll}>✕ Clear</button>}
      </div>

      {workbookSheetNames.length > 0 && (
        <div className="dc__sheet-switcher" aria-label="Workbook sheets">
          <span className="dc__sheet-switcher-label">Sheets</span>
          <div className="dc__sheet-tabs">
            {workbookSheetNames.map(sheetName => {
              const isActive = leftSheet === sheetName || rightSheet === sheetName;
              const existsLeft = hasSheet(leftSheets, sheetName);
              const existsRight = hasSheet(rightSheets, sheetName);
              return (
                <button
                  key={sheetName}
                  className={`dc__sheet-tab ${isActive ? 'dc__sheet-tab--active' : ''}`}
                  onClick={() => selectMatchingSheet(sheetName)}
                  title={!existsLeft ? 'Only in modified workbook' : !existsRight ? 'Only in original workbook' : 'Open this sheet in both workbooks'}
                >
                  {sheetName}
                  {(!existsLeft || !existsRight) && (
                    <span className={`dc__sheet-badge ${existsLeft ? 'dc__sheet-badge--removed' : 'dc__sheet-badge--added'}`}>
                      {existsLeft ? 'original only' : 'modified only'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="dc__sheet-selectors">
            {leftSheetNames.length > 0 && (
              <label>
                Original
                <select value={leftSheet} onChange={e => setLeftSheet(e.target.value)}>
                  {leftSheetNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
            )}
            {rightSheetNames.length > 0 && (
              <label>
                Modified
                <select value={rightSheet} onChange={e => setRightSheet(e.target.value)}>
                  {rightSheetNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
      )}

      {leftData && rightData && (
        <div className="dc__excel-stats">
          <span className="dc__stat dc__stat--added">+{stats.added} Added</span>
          <span className="dc__stat dc__stat--removed">−{stats.removed} Removed</span>
          <span className="dc__stat dc__stat--modified">~{stats.modified} Modified</span>
          <span className="dc__excel-stat-unchanged">{stats.unchanged} Unchanged</span>
          <div className="dc__zoom-controls">
            <button className="dc__zoom-btn" onClick={zoomOut} title="Zoom out">−</button>
            <button className="dc__zoom-label" onClick={zoomReset} title="Reset zoom">{zoom}%</button>
            <button className="dc__zoom-btn" onClick={zoomIn} title="Zoom in">+</button>
          </div>
          <button className="ec__panel-fullscreen" onClick={() => setFullscreen(true)} title="Fullscreen">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
          </button>
          <span className="dc__excel-files">{leftName} [{leftSheet}] → {rightName} [{rightSheet}]</span>
        </div>
      )}

      {/* Side-by-side panels - always visible */}
      <div className="dc__excel-compare" style={{ fontSize: `${zoom}%` }}>
        <div className="dc__excel-panel">
          <div className="dc__excel-panel-header">
            <span className="dc__excel-panel-title">Original</span>
            <span className="dc__excel-panel-file">{leftName ? `${leftName} · ${leftSheet}` : 'Paste or upload'}</span>
          </div>
          {leftData ? (
            renderTable(leftData, 'left', leftScrollRef, handleScroll('left'))
          ) : (
            <div className="dc__excel-paste-area" tabIndex={0} onPaste={handleTablePaste('left')}>
              <div className="dc__excel-paste-hint">
                <span className="dc__excel-paste-icon">⎘</span>
                <span>Click here & press <kbd>Ctrl+V</kbd> to paste</span>
                <span className="dc__excel-paste-sub">Supports Excel, Google Sheets, CSV</span>
                <label className="dc__toolbar-btn dc__toolbar-btn--sm">or Upload file<input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFile('left')} hidden /></label>
              </div>
            </div>
          )}
        </div>

        <div className="dc__excel-panel">
          <div className="dc__excel-panel-header">
            <span className="dc__excel-panel-title">Modified</span>
            <span className="dc__excel-panel-file">{rightName ? `${rightName} · ${rightSheet}` : 'Paste or upload'}</span>
          </div>
          {rightData ? (
            renderTable(rightData, 'right', rightScrollRef, handleScroll('right'))
          ) : (
            <div className="dc__excel-paste-area" tabIndex={0} onPaste={handleTablePaste('right')}>
              <div className="dc__excel-paste-hint">
                <span className="dc__excel-paste-icon">⎘</span>
                <span>Click here & press <kbd>Ctrl+V</kbd> to paste</span>
                <span className="dc__excel-paste-sub">Supports Excel, Google Sheets, CSV</span>
                <label className="dc__toolbar-btn dc__toolbar-btn--sm">or Upload file<input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFile('right')} hidden /></label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen overlay - side by side */}
      {fullscreen && (
        <div className="ec__fullscreen-overlay">
          <div className="ec__fullscreen-header">
            <span className="ec__fullscreen-title">{leftName} [{leftSheet}] ↔ {rightName} [{rightSheet}]</span>
            <div className="dc__zoom-controls">
              <button className="dc__zoom-btn" onClick={zoomOut} title="Zoom out">−</button>
              <button className="dc__zoom-label" onClick={zoomReset} title="Reset zoom">{zoom}%</button>
              <button className="dc__zoom-btn" onClick={zoomIn} title="Zoom in">+</button>
            </div>
            <button className="ec__fullscreen-close" onClick={() => setFullscreen(false)} title="Exit fullscreen">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" /></svg>
            </button>
          </div>
          <div className="ec__fullscreen-body" style={{ fontSize: `${zoom}%` }}>
            <div className="ec__fullscreen-panel">
              <div className="dc__excel-panel-header"><span className="dc__excel-panel-title">Original</span><span className="dc__excel-panel-file">{leftName} · {leftSheet}</span></div>
              {renderTable(leftData, 'left', leftScrollRef, handleScroll('left'))}
            </div>
            <div className="ec__fullscreen-panel">
              <div className="dc__excel-panel-header"><span className="dc__excel-panel-title">Modified</span><span className="dc__excel-panel-file">{rightName} · {rightSheet}</span></div>
              {renderTable(rightData, 'right', rightScrollRef, handleScroll('right'))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExcelCompare() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  useToolTheme();
  const [tabs, setTabs] = useState(() => {
    const saved = storage.get('ec_tabs');
    if (saved) { tabIdCounter = Math.max(...saved.map(t => t.id)) + 1; return saved; }
    return [createTab('Compare 1')];
  });
  const [activeTabId, setActiveTabId] = useState(() => storage.get('ec_activeTab') || tabs[0]?.id);
  const [editingTabId, setEditingTabId] = useState(null);

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  const addTab = () => {
    const newTab = createTab(`Compare ${tabs.length + 1}`);
    const next = [...tabs, newTab];
    setTabs(next);
    setActiveTabId(newTab.id);
    storage.set('ec_tabs', next);
    storage.set('ec_activeTab', newTab.id);
  };

  const closeTab = (id) => {
    if (tabs.length === 1) return;
    const next = tabs.filter(t => t.id !== id);
    setTabs(next);
    storage.set('ec_tabs', next);
    storage.remove(`ec_tab_${id}`);
    if (activeTabId === id) {
      const newActive = next[next.length - 1].id;
      setActiveTabId(newActive);
      storage.set('ec_activeTab', newActive);
    }
  };

  const renameTab = (id, name) => {
    setTabs(prev => {
      const next = prev.map(t => t.id === id ? { ...t, name } : t);
      storage.set('ec_tabs', next);
      return next;
    });
    setEditingTabId(null);
  };

  return (
    <div className="ec">
      <SEO title="Excel Compare Online | Diff Two Spreadsheets Side by Side - DevToolsDeck" description="Compare two multi-sheet Excel workbooks or CSV files side by side with highlighted cell differences. Your files stay in the browser." />
      <header className="ec__header">
        <div className="ec__header-left">
          <button className="ec__back" onClick={() => navigate('/')} title="Back to DevToolsDeck">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="ec__sheets-icon" aria-hidden="true">
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none">
              <path d="M6 2h8l4 4v16H6z" fill="currentColor" />
              <path d="M14 2v5h5M8.5 11h7M8.5 14h7M8.5 17h7M11 9v10" stroke="white" strokeWidth="1.2" />
            </svg>
          </div>
          <div className="ec__document-info">
            <div className="ec__document-title-row">
              <h1 className="ec__title">Excel Compare</h1>
              <span className="ec__saved-state" title="Workbook data is stored only in this browser">Saved locally</span>
            </div>
            <div className="ec__document-menu" aria-label="Application information">
              <span>Two-workbook comparison</span>
              <span className="ec__document-menu-dot">•</span>
              <span>Private</span>
            </div>
          </div>
        </div>
        <div className="ec__header-right">
          <a href="/" className="ec__brand" title="DevToolsDeck home">
            <Logo3D size={25} />
            <span>DevToolsDeck</span>
          </a>
          <button className="ec__icon-btn" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            )}
          </button>
        </div>
      </header>

      {/* Chrome-style Tab Bar */}
      <div className="ec__chrome-tabs">
        <div className="ec__chrome-tabs-row">
          <span className="ec__comparison-label">Comparisons</span>
          <div className="ec__chrome-tabs-list">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className={`ec__chrome-tab ${tab.id === activeTabId ? 'ec__chrome-tab--active' : ''}`}
                onClick={() => { setActiveTabId(tab.id); storage.set('ec_activeTab', tab.id); }}
              >
                {editingTabId === tab.id ? (
                  <input
                    className="ec__chrome-tab-input"
                    defaultValue={tab.name}
                    autoFocus
                    onBlur={e => renameTab(tab.id, e.target.value || tab.name)}
                    onKeyDown={e => { if (e.key === 'Enter') renameTab(tab.id, e.target.value || tab.name); }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span className="ec__chrome-tab-name">{tab.name}</span>
                )}
                <button className="ec__chrome-tab-edit" onClick={e => { e.stopPropagation(); setEditingTabId(tab.id); }} title="Rename tab">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                {tabs.length > 1 && (
                  <button className="ec__chrome-tab-close" onClick={e => { e.stopPropagation(); closeTab(tab.id); }}>×</button>
                )}
              </div>
            ))}
            <button className="ec__chrome-tab-add" onClick={addTab} title="New tab">+</button>
          </div>
        </div>
      </div>

      <ExcelDiff key={activeTab.id} tabId={activeTab.id} />
    </div>
  );
}

export default ExcelCompare;
